import React from 'react';
import PageTemplate from '../components/layout/PageTemplate';
import './Terms.css';

/** Shown as "last updated". Bump when the text below changes — a takedown
 *  notice is stronger when the terms visibly predate the copy. */
const LAST_UPDATED = '2026-08-25';

const CONTACT_EMAIL = 'capt.luje@gmail.com';

const TXT = {
    title: { fr: 'Conditions d\'utilisation', en: 'Terms of use', de: 'Nutzungsbedingungen', es: 'Condiciones de uso', pt: 'Condições de uso' },
    updated: { fr: 'Dernière mise à jour', en: 'Last updated', de: 'Zuletzt aktualisiert', es: 'Última actualización', pt: 'Última atualização' },

    ownershipTitle: { fr: 'Propriété', en: 'Ownership', de: 'Eigentum', es: 'Propiedad', pt: 'Propriedade' },
    ownershipBody: {
        fr: 'dofgen (dofgen.com) est une œuvre originale de Luje. Le site, son code, son interface, ses textes et les fichiers qu\'il distribue sont protégés par le droit d\'auteur. © 2026 Luje, tous droits réservés.',
        en: 'dofgen (dofgen.com) is an original work by Luje. The site, its code, its interface, its texts and the files it distributes are protected by copyright. © 2026 Luje, all rights reserved.',
        de: 'dofgen (dofgen.com) ist ein Originalwerk von Luje. Die Website, ihr Code, ihre Oberfläche, ihre Texte und die von ihr ausgelieferten Dateien sind urheberrechtlich geschützt. © 2026 Luje, alle Rechte vorbehalten.',
        es: 'dofgen (dofgen.com) es una obra original de Luje. El sitio, su código, su interfaz, sus textos y los archivos que distribuye están protegidos por derechos de autor. © 2026 Luje, todos los derechos reservados.',
        pt: 'O dofgen (dofgen.com) é uma obra original de Luje. O site, o seu código, a sua interface, os seus textos e os ficheiros que distribui estão protegidos por direitos de autor. © 2026 Luje, todos os direitos reservados.',
    },

    gameTitle: { fr: 'Contenu Dofus', en: 'Dofus content', de: 'Dofus-Inhalte', es: 'Contenido de Dofus', pt: 'Conteúdo Dofus' },
    gameBody: {
        fr: 'dofgen est un projet non officiel, sans lien avec Ankama Games. Les noms, items, caractéristiques, icônes et illustrations issus de Dofus restent la propriété d\'Ankama Games ; rien sur cette page ne s\'y applique.',
        en: 'dofgen is an unofficial project, unaffiliated with Ankama Games. Dofus names, items, characteristics, icons and artwork remain the property of Ankama Games; nothing on this page applies to them.',
        de: 'dofgen ist ein inoffizielles Projekt ohne Verbindung zu Ankama Games. Dofus-Namen, -Gegenstände, -Eigenschaften, -Symbole und -Grafiken bleiben Eigentum von Ankama Games; nichts auf dieser Seite gilt für sie.',
        es: 'dofgen es un proyecto no oficial, sin vínculo con Ankama Games. Los nombres, objetos, características, iconos e ilustraciones de Dofus siguen siendo propiedad de Ankama Games; nada en esta página se les aplica.',
        pt: 'O dofgen é um projeto não oficial, sem ligação com a Ankama Games. Os nomes, itens, características, ícones e ilustrações do Dofus continuam a ser propriedade da Ankama Games; nada nesta página se lhes aplica.',
    },

    useTitle: { fr: 'Utilisation', en: 'Use', de: 'Nutzung', es: 'Uso', pt: 'Uso' },
    useAllowed: {
        fr: 'Utilisez dofgen librement et gratuitement, pour jouer comme pour créer du contenu : partagez vos stuffs, leurs liens et des captures d\'écran, y compris sur des vidéos ou streams monétisés. Aucune autorisation n\'est nécessaire.',
        en: 'You may use dofgen freely and free of charge, to play and to create content: share your builds, their links and screenshots, including in monetised videos or streams. No permission is required.',
        de: 'Nutzen Sie dofgen frei und kostenlos, zum Spielen wie zum Erstellen von Inhalten: Teilen Sie Ihre Builds, deren Links und Screenshots, auch in monetarisierten Videos oder Streams. Eine Erlaubnis ist nicht erforderlich.',
        es: 'Use dofgen libremente y gratuitamente, para jugar y para crear contenido: comparta sus builds, sus enlaces y capturas de pantalla, incluso en vídeos o streams monetizados. No se requiere autorización.',
        pt: 'Use o dofgen livremente e gratuitamente, para jogar e para criar conteúdo: partilhe as suas builds, os seus links e capturas de ecrã, incluindo em vídeos ou streams monetizados. Não é necessária autorização.',
    },
    forbiddenIntro: {
        fr: 'En revanche, sans autorisation écrite, il est interdit de :',
        en: 'You may not, however, without written permission:',
        de: 'Ohne schriftliche Erlaubnis ist es jedoch untersagt:',
        es: 'En cambio, sin autorización escrita, no está permitido:',
        pt: 'Em contrapartida, sem autorização escrita, não é permitido:',
    },
    forbiddenItems: {
        fr: [
            'copier, réhéberger ou redéployer tout ou partie de dofgen',
            'réutiliser ailleurs son code, son interface ou les fichiers qu\'il sert (noyau du solveur, catalogue d\'items)',
            'retirer ou altérer les mentions de droits d\'auteur qu\'il contient',
        ],
        en: [
            'copy, re-host or redeploy dofgen or any part of it',
            'reuse elsewhere its code, its interface or the files it serves (solver kernel, item catalog)',
            'remove or alter the copyright notices it carries',
        ],
        de: [
            'dofgen ganz oder teilweise zu kopieren, erneut zu hosten oder neu zu veröffentlichen',
            'seinen Code, seine Oberfläche oder die von ihm ausgelieferten Dateien (Solver-Kernel, Gegenstandskatalog) anderswo zu verwenden',
            'die enthaltenen Urheberrechtshinweise zu entfernen oder zu verändern',
        ],
        es: [
            'copiar, rehospedar o volver a desplegar dofgen o cualquier parte de él',
            'reutilizar en otro lugar su código, su interfaz o los archivos que sirve (núcleo del solver, catálogo de objetos)',
            'quitar o alterar los avisos de derechos de autor que contiene',
        ],
        pt: [
            'copiar, re-hospedar ou reimplantar o dofgen ou qualquer parte dele',
            'reutilizar noutro local o seu código, a sua interface ou os ficheiros que serve (núcleo do solver, catálogo de itens)',
            'remover ou alterar os avisos de direitos de autor que contém',
        ],
    },
    forbiddenNote: {
        fr: 'Le code exécuté par votre navigateur ne tombe pas pour autant dans le domaine public : il vous est transmis pour faire fonctionner le site. Chaque build porte des marqueurs permettant d\'établir l\'origine d\'une copie, et les copies sont signalées à leur hébergeur.',
        en: 'Code running in your browser is not thereby in the public domain: it is sent to you so the site can work. Every build carries markers that establish where a copy came from, and copies are reported to their host.',
        de: 'Code, der in Ihrem Browser läuft, ist deshalb nicht gemeinfrei: Er wird Ihnen übermittelt, damit die Website funktioniert. Jeder Build trägt Markierungen, die die Herkunft einer Kopie belegen, und Kopien werden bei ihrem Hoster gemeldet.',
        es: 'El código que se ejecuta en su navegador no pasa por ello al dominio público: se le envía para que el sitio funcione. Cada build lleva marcadores que permiten establecer el origen de una copia, y las copias se denuncian a su alojamiento.',
        pt: 'O código executado no seu navegador não passa por isso ao domínio público: é-lhe enviado para que o site funcione. Cada build leva marcadores que permitem estabelecer a origem de uma cópia, e as cópias são denunciadas ao seu alojamento.',
    },

    contactTitle: { fr: 'Contact', en: 'Contact', de: 'Kontakt', es: 'Contacto', pt: 'Contacto' },
    contactBody: {
        fr: 'Demande d\'autorisation, question ou signalement d\'une copie :',
        en: 'Permission requests, questions or reports of a copy:',
        de: 'Genehmigungsanfragen, Fragen oder Meldung einer Kopie:',
        es: 'Solicitudes de autorización, preguntas o denuncia de una copia:',
        pt: 'Pedidos de autorização, questões ou denúncia de uma cópia:',
    },
    prevailing: {
        fr: 'En cas de divergence entre les traductions, la version française fait foi.',
        en: 'In case of discrepancy between translations, the French version prevails.',
        de: 'Bei Abweichungen zwischen den Übersetzungen ist die französische Fassung maßgeblich.',
        es: 'En caso de discrepancia entre las traducciones, prevalece la versión francesa.',
        pt: 'Em caso de divergência entre as traduções, prevalece a versão francesa.',
    },
};

function t(dict, lang) {
    return dict[lang] || dict.fr;
}

/** The product name is set apart wherever it appears in the prose. Done by
 *  splitting the rendered string, not by marking up each translation: the
 *  tables above stay plain text in five languages, and a name added to a new
 *  sentence is styled without anyone remembering to do it.
 *  `dofgen.com` is matched before the bare name so the domain styles whole. */
const BRAND_RE = /(dofgen\.com|dofgen)/g;

function brand(text) {
    return text
        .split(BRAND_RE)
        .map((part, i) => (part === 'dofgen' || part === 'dofgen.com'
            ? <span className="terms-brand" key={i}>{part}</span>
            : part));
}

const Terms = ({ lang = 'fr' }) => (
    <PageTemplate header={<h1 className="page-template-title">{t(TXT.title, lang)}</h1>}>
        <div className="terms column">
            <p className="terms-fineprint">
                {t(TXT.updated, lang)} : {LAST_UPDATED}
            </p>

            <section className="terms-section column">
                <h2 className="section-title">{t(TXT.ownershipTitle, lang)}</h2>
                <p>{brand(t(TXT.ownershipBody, lang))}</p>
            </section>

            <section className="terms-section column">
                <h2 className="section-title">{t(TXT.gameTitle, lang)}</h2>
                <p>{brand(t(TXT.gameBody, lang))}</p>
            </section>

            <section className="terms-section column">
                <h2 className="section-title">{t(TXT.useTitle, lang)}</h2>
                <p>{brand(t(TXT.useAllowed, lang))}</p>
                <p>{t(TXT.forbiddenIntro, lang)}</p>
                <ul className="terms-list">
                    {t(TXT.forbiddenItems, lang).map((item) => (
                        <li key={item}>{brand(item)}</li>
                    ))}
                </ul>
                <p>{brand(t(TXT.forbiddenNote, lang))}</p>
            </section>

            <section className="terms-section column">
                <h2 className="section-title">{t(TXT.contactTitle, lang)}</h2>
                <p>
                    {t(TXT.contactBody, lang)}{' '}
                    <a className="terms-link" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
                </p>
                <p className="terms-fineprint">{t(TXT.prevailing, lang)}</p>
            </section>
        </div>
    </PageTemplate>
);

export default Terms;
